import React, { useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { PendingFilterSelection } from '../../../hooks/usePendingFilterSelection';
import { lastCompleteIsoDate } from '../topSellers/TopSellersReport.component';
import { InfoTooltip } from './InfoTooltip.component';

// Control styles shared with Vendor Performance and Top Sellers.
export const FILTER_BUTTON_CLASS =
  'h-[34px] w-full bg-white border border-[#D0D5DD] rounded-[9px] px-2.5 text-[13px] font-semibold text-[#344054] text-left leading-[20px] flex items-center justify-between gap-1.5 shadow-2xs hover:bg-[#F9FAFB] transition-colors cursor-pointer whitespace-nowrap';
export const FILTER_LABEL_CLASS = 'text-[12px] font-medium text-[#71717A] leading-[18px] whitespace-nowrap';
const POPOVER_CLASS =
  'absolute left-0 top-full mt-1.5 z-50 bg-white border border-[#E4E4E7] rounded-2xl shadow-xl animate-in fade-in zoom-in-95 duration-100';
const SEARCH_INPUT_CLASS =
  'h-8 w-full px-2.5 border border-[#E4E4E7] rounded-[8px] text-[13px] font-medium text-[#344054] bg-white focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs';
const OPTION_CLASS = 'w-full px-3 py-2 text-[13px] font-semibold rounded-[9px] flex items-center justify-between text-left transition-colors cursor-pointer leading-[21px]';
const optionStateClass = (isSelected: boolean) => (isSelected ? 'bg-[#F4F0FF] text-[#7F56D9]' : 'text-[#344054] hover:bg-[#F9FAFB]');
const DATE_INPUT_CLASS =
  'w-full px-2.5 py-1.5 text-[12px] font-medium text-[#344054] border border-[#D0D5DD] rounded-[8px] focus:outline-none focus:ring-1 focus:ring-[#7F56D9]';

export interface FilterOption {
  code: string;
  name: string;
}

/** A filter's caption, with an info icon when it has a description. */
export const FilterLabel: React.FC<{ label: string; info?: string }> = ({ label, info }) => (
  <div className="flex items-center">
    <span className={FILTER_LABEL_CLASS}>{label}</span>
    {info && <InfoTooltip text={info} position="bottom" align="left" />}
  </div>
);

/** The selected options' names, in selection order; a code with no loaded option shows as itself. */
export const selectedNames = (codes: string[], options: FilterOption[]): string[] =>
  codes.map((code) => options.find((option) => option.code === code)?.name || code);

/** Button text for a multi-select: "All vendors", the one selected name, or "Bottoms +1". */
export function multiSelectSummary(codes: string[], options: FilterOption[], allLabel: string): string {
  if (codes.length === 0) return allLabel;
  const [first] = selectedNames(codes, options);
  return codes.length === 1 ? first : `${first} +${codes.length - 1}`;
}

interface MultiSelectFilterProps {
  label: string;
  info: string;
  allLabel: string;
  // Singular, for the search box: "Search location...".
  noun: string;
  pluralNoun: string;
  options: FilterOption[];
  // The applied selection, for the button text.
  appliedCodes: string[];
  appliedExclude: boolean;
  draft: PendingFilterSelection;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  // Overrides the default name match, e.g. to also match a class code.
  matches?: (option: FilterOption, query: string) => boolean;
  allowExclude?: boolean;
  // Hides the search box for short fixed lists.
  searchable?: boolean;
}

const defaultMatches = (option: FilterOption, query: string): boolean =>
  option.name.toLowerCase().includes(query.trim().toLowerCase());

/**
 * A Location / Category / Class / Vendor filter: searchable checkboxes with "Exclude selected
 * instead", editing a draft that commits when the popover closes (see usePendingFilterSelection).
 */
export const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  label,
  info,
  allLabel,
  noun,
  pluralNoun,
  options,
  appliedCodes,
  appliedExclude,
  draft,
  isOpen,
  onToggle,
  onClose,
  matches = defaultMatches,
  allowExclude = true,
  searchable = true,
}) => {
  const [query, setQuery] = useState('');
  const visibleOptions = query.trim() ? options.filter((option) => matches(option, query)) : options;
  const summary = multiSelectSummary(appliedCodes, options, allLabel);

  return (
    <div className="flex flex-col gap-1 relative min-w-[120px] flex-1" data-popover-container>
      <FilterLabel label={label} info={info} />
      <button type="button" onClick={onToggle} aria-expanded={isOpen} aria-haspopup="dialog" className={FILTER_BUTTON_CLASS}>
        <span className="truncate">{appliedExclude && appliedCodes.length > 0 ? `Not ${summary}` : summary}</span>
        <ChevronDown className="w-3.5 h-3.5 text-[#667085] flex-shrink-0" />
      </button>

      {isOpen && (
        <div role="dialog" aria-label={`${label} filter`} className={`${POPOVER_CLASS} w-64 p-3.5 flex flex-col gap-2.5`}>
          <div className="text-[13px] font-bold text-[#101828]">{label}</div>
          {searchable && (
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#98A2B3] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={`Search ${noun}...`}
                aria-label={`Search ${noun}`}
                className={`${SEARCH_INPUT_CLASS} pl-8`}
              />
            </div>
          )}
          <div className="flex flex-col gap-1 max-h-56 overflow-y-auto pr-1">
            {visibleOptions.length === 0 && (
              <div className="text-[13px] text-[#94A3B8] italic px-1 py-2">No {pluralNoun} available</div>
            )}
            {visibleOptions.map((option) => (
              <label
                key={option.code}
                className="flex items-center gap-2.5 cursor-pointer text-[13px] font-semibold text-[#344054] hover:bg-[#F9FAFB] p-1 rounded-lg transition-colors"
              >
                <input
                  type="checkbox"
                  checked={draft.pending.codes.includes(option.code)}
                  onChange={() => draft.toggleCode(option.code)}
                  className="w-4 h-4 accent-[#7F56D9] text-[#7F56D9] rounded border-[#D0D5DD]"
                />
                <span className="truncate">{option.name}</span>
              </label>
            ))}
          </div>
          {allowExclude && (
            <label className="flex items-center gap-2.5 px-1 text-[13px] font-medium text-[#344054] cursor-pointer">
              <input
                type="checkbox"
                checked={draft.pending.exclude}
                onChange={(e) => draft.setExclude(e.target.checked)}
                className="w-4 h-4 accent-[#7F56D9] rounded border-[#D0D5DD]"
              />
              <span>Exclude selected instead</span>
            </label>
          )}
          <div className="border-t border-[#EAECF0] pt-2.5">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  draft.clearCodes();
                  setQuery('');
                }}
                className="text-[13px] font-semibold text-[#667085] hover:text-[#101828] cursor-pointer"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-1.5 bg-[#7F56D9] hover:bg-[#6941C6] text-white text-[13px] font-bold rounded-[9px] shadow-xs cursor-pointer"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface SingleSelectFilterProps<V> {
  label: string;
  info?: string;
  value: V;
  // `description` adds a line under the option, as in the Hierarchy menu.
  options: readonly { value: V; label: string; description?: string }[];
  isOpen: boolean;
  onToggle: () => void;
  onChange: (value: V) => void;
  // Wider menu for options with descriptions.
  menuWidthClass?: string;
  // Hides the caption, for a control that sits beside its label (the search field selector).
  hideLabel?: boolean;
  className?: string;
}

/** A dropdown that applies on pick: Age threshold, Aging tier, Hierarchy, search field. */
export function SingleSelectFilter<V extends string | number | null>({
  label,
  info,
  value,
  options,
  isOpen,
  onToggle,
  onChange,
  menuWidthClass = 'w-56',
  hideLabel = false,
  className = 'min-w-[120px] flex-1',
}: SingleSelectFilterProps<V>) {
  const selected = options.find((option) => option.value === value);
  return (
    <div className={`flex flex-col gap-1 relative ${className}`} data-popover-container>
      {!hideLabel && <FilterLabel label={label} info={info} />}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={hideLabel ? label : undefined}
        className={FILTER_BUTTON_CLASS}
      >
        <span className="truncate">{selected?.label ?? options[0]?.label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-[#667085] flex-shrink-0" />
      </button>
      {isOpen && (
        <div role="listbox" aria-label={label} className={`${POPOVER_CLASS} ${menuWidthClass} p-2.5 flex flex-col gap-1`}>
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={String(option.value)}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => onChange(option.value)}
                className={`${OPTION_CLASS} ${optionStateClass(isSelected)}`}
              >
                <span className="flex flex-col min-w-0">
                  <span>{option.label}</span>
                  {option.description && (
                    <span className="text-[12px] font-normal leading-[18px] text-[#667085] whitespace-normal">{option.description}</span>
                  )}
                </span>
                {isSelected && <Check className="w-4 h-4 text-[#7F56D9] shrink-0 self-start mt-0.5" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface DateRangeFilterProps {
  info: string;
  presets: readonly string[];
  customPreset: string;
  value: string;
  customStart: string;
  customEnd: string;
  isOpen: boolean;
  onToggle: () => void;
  onPreset: (preset: string) => void;
  onCustom: (from: string, to: string) => void;
}

/** Date range presets, plus a from/to pair for the custom preset. Ranges end at the last full day. */
export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  info,
  presets,
  customPreset,
  value,
  customStart,
  customEnd,
  isOpen,
  onToggle,
  onPreset,
  onCustom,
}) => {
  const [showCustom, setShowCustom] = useState(false);
  const [draftStart, setDraftStart] = useState(customStart);
  const [draftEnd, setDraftEnd] = useState(customEnd);
  const lastDay = lastCompleteIsoDate();
  const isCustom = value === customPreset;

  return (
    <div className="flex flex-col gap-1 relative min-w-[130px] flex-1" data-popover-container>
      <FilterLabel label="Date range" info={info} />
      <button
        type="button"
        onClick={() => {
          setDraftStart(customStart);
          setDraftEnd(customEnd);
          setShowCustom(isCustom);
          onToggle();
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className={FILTER_BUTTON_CLASS}
      >
        <span className="truncate">{isCustom && customStart && customEnd ? `${customStart} – ${customEnd}` : value}</span>
        <ChevronDown className="w-3.5 h-3.5 text-[#667085] flex-shrink-0" />
      </button>

      {isOpen && (
        <div role="listbox" aria-label="Date range" className={`${POPOVER_CLASS} w-60 p-2.5 flex flex-col gap-1`}>
          {presets.map((preset) => {
            const isSelected = preset === value || (preset === customPreset && showCustom);
            return (
              <button
                key={preset}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => (preset === customPreset ? setShowCustom(true) : onPreset(preset))}
                className={`${OPTION_CLASS} ${optionStateClass(isSelected)}`}
              >
                <span>{preset}</span>
                {isSelected && <Check className="w-4 h-4 text-[#7F56D9]" />}
              </button>
            );
          })}
          {showCustom && (
            <div className="mt-2 pt-2.5 border-t border-[#F2F4F7] flex flex-col gap-2">
              <label className="flex flex-col gap-1 text-[11px] font-semibold text-[#475467]">
                From
                <input type="date" max={draftEnd || lastDay} value={draftStart} onChange={(e) => setDraftStart(e.target.value)} className={DATE_INPUT_CLASS} />
              </label>
              <label className="flex flex-col gap-1 text-[11px] font-semibold text-[#475467]">
                To
                <input
                  type="date"
                  min={draftStart}
                  max={lastDay}
                  value={draftEnd}
                  onChange={(e) => setDraftEnd(e.target.value && e.target.value > lastDay ? lastDay : e.target.value)}
                  className={DATE_INPUT_CLASS}
                />
              </label>
              <button
                type="button"
                onClick={() => onCustom(draftStart, draftEnd)}
                disabled={!draftStart || !draftEnd}
                className="w-full mt-1 px-3 py-2 bg-[#7F56D9] text-white text-[12px] font-semibold rounded-[8px] hover:bg-[#6941C6] disabled:opacity-50 transition-colors cursor-pointer"
              >
                Apply Range
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface SegmentedControlProps<V extends string> {
  label: string;
  info?: string;
  value: V;
  options: readonly { value: V; label: string }[];
  onChange: (value: V) => void;
}

/** Two or more mutually exclusive choices shown side by side: Show SKUs [Product | SKU]. */
export function SegmentedControl<V extends string>({ label, info, value, options, onChange }: SegmentedControlProps<V>) {
  return (
    <div className="flex flex-col gap-1">
      <FilterLabel label={label} info={info} />
      <div role="radiogroup" aria-label={label} className="h-[34px] inline-flex items-center p-0.5 bg-[#F2F4F7] border border-[#E4E4E7] rounded-[9px]">
        {options.map((option) => {
          const isSelected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onChange(option.value)}
              className={`h-full px-3 rounded-[7px] text-[13px] font-semibold transition-colors cursor-pointer ${
                isSelected ? 'bg-white text-[#101828] shadow-2xs' : 'text-[#667085] hover:text-[#344054]'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface ToggleFilterProps {
  label: string;
  info?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

/** A switch with its label beside it, aligned with the bottom of the labelled controls. */
export const ToggleFilter: React.FC<ToggleFilterProps> = ({ label, info, checked, onChange }) => (
  <div className="h-[34px] flex items-center gap-2 self-end">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
        checked ? 'bg-[#7F56D9]' : 'bg-[#E4E4E7]'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`}
      />
    </button>
    <span className="text-[13px] font-medium text-[#344054] whitespace-nowrap">{label}</span>
    {info && <InfoTooltip text={info} position="bottom" align="left" />}
  </div>
);

interface SearchInputProps {
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
}

export const SearchInput: React.FC<SearchInputProps> = ({ value, placeholder, onChange }) => (
  <div className="relative">
    <Search className="w-4 h-4 text-[#98A2B3] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={placeholder.replace(/\.+$/, '')}
      className="h-[34px] w-56 pl-8 pr-3 bg-white border border-[#D0D5DD] rounded-[9px] text-[13px] font-medium text-[#344054] focus:outline-none focus:ring-2 focus:ring-[#7F56D9] shadow-2xs"
    />
  </div>
);

export interface FilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

/** The applied filters as removable chips, with Clear all. Renders nothing when no filter is set. */
export const FilterChips: React.FC<{ chips: FilterChip[]; onClearAll: () => void }> = ({ chips, onClearAll }) => {
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Active filters">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1.5 rounded-full bg-[#F4F0FF] border border-[#E9D7FE] text-[12px] font-semibold text-[#6941C6]"
        >
          <span className="truncate max-w-[220px]">{chip.label}</span>
          <button
            type="button"
            onClick={chip.onRemove}
            aria-label={`Remove filter ${chip.label}`}
            className="p-0.5 rounded-full text-[#9E77ED] hover:text-[#53389E] hover:bg-[#E9D7FE] cursor-pointer"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <button type="button" onClick={onClearAll} className="text-[12px] font-semibold text-[#667085] hover:text-[#101828] cursor-pointer px-1">
        Clear all
      </button>
    </div>
  );
};
